<template>
  <div v-if="visible" data-testid="email-verification-banner">
    <Notification
      :message="notification"
      :trigger-notification="showNotification"
      class="w-screen fixed left-0 top-15 z-50"
      @toggle-visibility="showNotification = false"
    />
    <AlertBanner
      :status="helperStatus.Warn"
      class="banner-below-nav !leading-10"
      :full-page-banner="true"
      :auto-scroll-to-banner="false"
    >
      <span class="inline-block px-16 py-8">
        Verify your email with the link sent to
        <span class="font-medium" data-testid="email-verification-banner-address">{{ displayedEmail }}</span
        >.
        <button
          type="button"
          class="underline font-medium cursor-pointer"
          data-testid="email-verification-banner-resend"
          :disabled="resending"
          @click="resendVerificationEmail"
        >
          Resend link
        </button>
      </span>
    </AlertBanner>
    <!-- spacer: banner is position:fixed below the nav -->
    <div class="h-52 md-1:h-56 w-full lg:hidden" aria-hidden="true">&nbsp;</div>
    <div class="hidden lg:block h-40 w-full" aria-hidden="true">&nbsp;</div>
  </div>
</template>

<script lang="ts">
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import Notification from '@/components/notification/Notification.vue';
import helperStatus from '@/helpers/status';
import shouldShowEmailVerificationBanner from '@/helpers/emailVerificationBannerLogic';
import { shouldForceParentExperimentEmailVerificationBanner } from '@/helpers/shouldForceParentExperimentEmailVerificationBanner';
import { EMAIL_VERIFICATION_STATUS } from '@/graphql/account/queries';
import { SEND_VERIFICATION_EMAIL } from '@/graphql/account/mutations';
import { EMAIL_VERIFICATION_POLL_INTERVAL_MS } from '@/apps/verifyEmail/useVerifyEmailWaiting';
import { invalidateEmailVerificationStatus } from '@/apps/verifyEmail/helpers/emailVerificationStatus';

type GraphQLErrorLike = {
  message: string;
  extensions?: { error_code?: string };
};

export default {
  name: 'EmailVerificationBanner',
  components: {
    AlertBanner,
    Notification,
  },
  emits: ['visibility-change'],
  data() {
    return {
      helperStatus,
      showBannerFromApi: false,
      forceShowFromExperiment: false,
      email: '',
      loaded: false,
      resending: false,
      notification: '',
      showNotification: false,
      pollTimer: null as ReturnType<typeof setTimeout> | null,
      wasVisible: false,
    };
  },
  computed: {
    visible(): boolean {
      if (!this.loaded) {
        return false;
      }
      return shouldShowEmailVerificationBanner(this.showBannerFromApi, this.$route.path, {
        forceShow: this.forceShowFromExperiment,
      });
    },
    displayedEmail(): string {
      return this.email || 'your email address';
    },
  },
  watch: {
    '$route.fullPath'() {
      void this.refreshStatus();
    },
    visible: {
      immediate: true,
      handler(isVisible: boolean) {
        this.$emit('visibility-change', isVisible);
        if (isVisible) {
          this.wasVisible = true;
          this.scheduleNextPoll();
          return;
        }
        if (this.wasVisible) {
          invalidateEmailVerificationStatus();
          this.wasVisible = false;
        }
        this.stopPolling();
      },
    },
  },
  mounted() {
    void this.refreshStatus();
    window.addEventListener('focus', this.onWindowFocus);
  },
  beforeUnmount() {
    window.removeEventListener('focus', this.onWindowFocus);
    this.stopPolling();
    this.$emit('visibility-change', false);
  },
  methods: {
    onWindowFocus(): void {
      void this.refreshStatus();
    },
    scheduleNextPoll(): void {
      if (!this.visible) {
        return;
      }
      this.stopPolling();
      this.pollTimer = setTimeout(() => {
        void this.pollOnce();
      }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);
    },
    async pollOnce(): Promise<void> {
      if (!this.visible) {
        return;
      }
      await this.refreshStatus();
      if (this.visible) {
        this.scheduleNextPoll();
      }
    },
    stopPolling(): void {
      if (this.pollTimer !== null) {
        clearTimeout(this.pollTimer);
        this.pollTimer = null;
      }
    },
    async refreshStatus(): Promise<void> {
      try {
        const { data } = await this.$apollo.query({
          query: EMAIL_VERIFICATION_STATUS,
          fetchPolicy: 'network-only',
        });
        const status = data?.emailVerificationStatus;
        const verified = !!status?.verified;
        this.showBannerFromApi = !!status?.showBanner;
        this.forceShowFromExperiment = shouldForceParentExperimentEmailVerificationBanner(
          verified,
          !!status?.emailVerificationRequired
        );
        if (status?.email) {
          this.email = status.email;
        }
      } catch (error) {
        console.error('Error checking email verification banner eligibility:', error);
        this.showBannerFromApi = false;
        this.forceShowFromExperiment = false;
      } finally {
        this.loaded = true;
      }
    },
    async resendVerificationEmail(): Promise<void> {
      if (this.resending) {
        return;
      }

      this.resending = true;
      try {
        await this.$apollo.mutate({
          mutation: SEND_VERIFICATION_EMAIL,
        });
        this.notify("Verification email re-sent. Check your email to verify and don't forget to check your spam folder.");
      } catch (error: unknown) {
        const graphqlError = (error as { graphQLErrors?: GraphQLErrorLike[] })?.graphQLErrors?.[0];
        if (graphqlError?.extensions?.error_code === 'rate_limit_exceeded') {
          this.notify(graphqlError.message);
        } else {
          this.notify('Something went wrong sending your verification email. Please try again.');
          console.error(error);
        }
      } finally {
        this.resending = false;
      }
    },
    notify(message: string): void {
      this.notification = message;
      this.showNotification = true;
    },
  },
};
</script>
